import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Braces,
  Check,
  ChevronDown,
  ChevronUp,
  Code2,
  Info,
  ListChecks,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import {
  buildPtdFieldModel,
  getProductTypeSchemaLink,
  getPtdFieldValue,
  missingRequiredPtdFields,
  resolveProductTypeSchema,
  setPtdFieldValue,
  validatePtdFieldValue,
  type PtdField,
} from "../productTypeSchema";
import type { ProductTypeDefinitionResponse } from "../types";
import {
  Button,
  Field,
  InlineError,
  Input,
  Select,
  StatePanel,
  StatusPill,
  Textarea,
} from "./ui";

const EXPERT_FORBIDDEN_KEYS = new Set([
  "condition_type",
  "conditiontype",
  "fulfillment_availability",
  "fulfillmentavailability",
  "merchant_suggested_asin",
  "merchantsuggestedasin",
  "purchasable_offer",
  "purchasableoffer",
  "merchant_shipping_group",
  "merchantshippinggroup",
  "quantity",
  "inventory",
  "price",
  "prices",
  "start_price",
  "startprice",
  "start_prices",
  "startprices",
  "shipping",
  "shipping_template",
  "shippingtemplate",
  "shipping_templates",
  "shippingtemplates",
  "fulfillment_mode",
  "fulfillmentmode",
]);

export function ProductTypeAttributesForm({
  productType,
  definition,
  loading,
  error,
  attributes,
  onChange,
  onRetry,
}: {
  productType: string;
  definition: ProductTypeDefinitionResponse | null;
  loading: boolean;
  error: string | null;
  attributes: Record<string, unknown>;
  onChange: (attributes: Record<string, unknown>) => void;
  onRetry: () => void;
}) {
  const [showOptional, setShowOptional] = useState(false);
  const [expertOpen, setExpertOpen] = useState(false);
  const [expertJson, setExpertJson] = useState("{}");
  const [expertError, setExpertError] = useState<string | null>(null);
  const [expertSuccess, setExpertSuccess] = useState<string | null>(null);

  const schema = useMemo(() => resolveProductTypeSchema(definition), [definition]);
  const model = useMemo(
    () => (definition && schema ? buildPtdFieldModel(definition, schema) : null),
    [definition, schema],
  );
  const schemaLink = useMemo(() => getProductTypeSchemaLink(definition), [definition]);
  const missingRequired = useMemo(
    () => (model ? missingRequiredPtdFields(model, attributes) : []),
    [model, attributes],
  );
  const visibleFields = useMemo(
    () => model?.fields.filter((field) => field.required || showOptional) ?? [],
    [model, showOptional],
  );
  const groupedFields = useMemo(() => {
    const result = new Map<string, { title: string; fields: PtdField[] }>();
    for (const field of visibleFields) {
      const group = result.get(field.groupKey) ?? { title: field.groupTitle, fields: [] };
      group.fields.push(field);
      result.set(field.groupKey, group);
    }
    return [...result.entries()];
  }, [visibleFields]);

  useEffect(() => {
    if (!expertOpen) {
      setExpertJson(JSON.stringify(attributes, null, 2));
      setExpertError(null);
      setExpertSuccess(null);
    }
  }, [attributes, expertOpen]);

  useEffect(() => {
    if (!model) return;
    let next = attributes;
    let changed = false;
    for (const field of model.requiredFields) {
      const current = getPtdFieldValue(next, field);
      if (
        (current === undefined || current === null || current === "") &&
        field.defaultValue !== undefined
      ) {
        next = setPtdFieldValue(next, field, field.defaultValue);
        changed = true;
      }
    }
    if (changed) onChange(next);
  }, [model, attributes, onChange]);

  const applyExpertJson = () => {
    setExpertError(null);
    setExpertSuccess(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(expertJson);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "Ungültiges JSON";
      setExpertError(`JSON konnte nicht gelesen werden: ${message}`);
      return;
    }
    if (!isPlainRecord(parsed)) {
      setExpertError("Der Expertenmodus erwartet ein JSON-Objekt.");
      return;
    }
    const candidate = isPlainRecord(parsed.attributes) ? parsed.attributes : parsed;
    const forbidden = Object.keys(candidate).filter((key) =>
      EXPERT_FORBIDDEN_KEYS.has(key.toLowerCase()),
    );
    if (forbidden.length) {
      setExpertError(
        `Nicht erlaubt: ${forbidden.join(
          ", ",
        )}. Bestand, Preis und Versand bleiben ausschließlich servergesteuert.`,
      );
      return;
    }
    const merged = { ...attributes, ...candidate };
    onChange(merged);
    setExpertJson(JSON.stringify(merged, null, 2));
    setExpertSuccess(`${Object.keys(candidate).length} Attribute wurden in den Entwurf übernommen.`);
  };

  const formatExpertJson = () => {
    setExpertError(null);
    try {
      const parsed = JSON.parse(expertJson);
      if (!isPlainRecord(parsed)) throw new Error("Das JSON muss ein Objekt sein.");
      setExpertJson(JSON.stringify(parsed, null, 2));
    } catch (reason) {
      setExpertError(reason instanceof Error ? reason.message : "Ungültiges JSON.");
    }
  };

  return (
    <div className="ptd-form">
      <div className="ptd-form__identity">
        <span><Sparkles size={18} /></span>
        <div>
          <strong>{definition?.displayName ?? productType}</strong>
          <small>
            Produkttyp {definition?.productType ?? productType} · Amazon DE ·{" "}
            {definition?.requirements ?? "LISTING"}
          </small>
        </div>
        {schema ? (
          <StatusPill status="success" label="Schema aufgelöst" />
        ) : loading ? (
          <StatusPill status="pending" label="Schema wird geladen" />
        ) : (
          <StatusPill status="warning" label="Expertenmodus verfügbar" />
        )}
      </div>

      {loading && !definition ? (
        <StatePanel
          type="loading"
          title="Amazon-Produkttyp wird geladen"
          description="Pflichtfelder, Wertelisten und Validierungsregeln werden serverseitig aus dem PTD-Schema aufgelöst."
        />
      ) : error ? (
        <>
          <InlineError message={error} onRetry={onRetry} />
          <FallbackCatalogFields attributes={attributes} onChange={onChange} />
        </>
      ) : schema && model ? (
        <>
          <div className="ptd-summary">
            <div>
              <span><ListChecks size={17} /></span>
              <p>
                <strong>{model.requiredFields.length} einfache Pflichtfelder</strong>
                <small>
                  {missingRequired.length
                    ? `${missingRequired.length} Angaben fehlen noch`
                    : "Alle renderbaren Pflichtfelder sind ausgefüllt"}
                </small>
              </p>
            </div>
            <div>
              <span><Braces size={17} /></span>
              <p>
                <strong>{model.fields.length} nutzbare Schemafelder</strong>
                <small>Strings, Zahlen, Ja/Nein und Amazon-Wertelisten</small>
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowOptional((current) => !current)}
              disabled={!model.fields.some((field) => !field.required)}
            >
              {showOptional ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              {showOptional ? "Optionale Felder ausblenden" : "Optionale Felder anzeigen"}
            </button>
          </div>

          {model.complexRequiredAttributes.length > 0 && (
            <div className="ptd-complex-warning">
              <AlertTriangle size={18} />
              <div>
                <strong>Komplexe Amazon-Pflichtstrukturen erkannt</strong>
                <p>
                  {model.complexRequiredAttributes.join(", ")} können nicht sicher als
                  einfache Eingabefelder dargestellt werden. Ergänze sie strukturiert im
                  JSON-Expertenmodus; die Server-Vorschau validiert sie anschließend erneut.
                </p>
              </div>
              <Button variant="secondary" onClick={() => setExpertOpen(true)} icon={<Code2 size={15} />}>
                Expertenmodus
              </Button>
            </div>
          )}

          {groupedFields.length ? (
            <div className="ptd-groups">
              {groupedFields.map(([groupKey, group]) => (
                <section key={groupKey} className="ptd-group">
                  <header>
                    <div>
                      <h4>{group.title}</h4>
                      <span>
                        {group.fields.filter((field) => field.required).length} Pflicht ·{" "}
                        {group.fields.length} sichtbar
                      </span>
                    </div>
                  </header>
                  <div className="ptd-field-grid">
                    {group.fields.map((field) => (
                      <DynamicPtdField
                        key={field.id}
                        field={field}
                        value={getPtdFieldValue(attributes, field)}
                        onChange={(value) =>
                          onChange(setPtdFieldValue(attributes, field, value))
                        }
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <StatePanel
              type="empty"
              title="Keine einfachen Schemafelder"
              description="Dieser Produkttyp besteht ausschließlich aus komplexen Strukturen. Verwende den JSON-Expertenmodus und prüfe den Entwurf anschließend serverseitig."
            />
          )}
        </>
      ) : (
        <>
          <div className="ptd-schema-contract">
            <AlertTriangle size={19} />
            <div>
              <strong>Aufgelöstes PTD-Schema fehlt</strong>
              <p>
                {schemaLink
                  ? "Amazon hat nur einen temporären Schema-Link geliefert. Der Browser lädt diesen bewusst nicht direkt; der API Server muss das JSON-Schema auflösen."
                  : "Die Produkttyp-Antwort enthält kein verwendbares JSON-Schema."}
              </p>
            </div>
            <Button variant="secondary" onClick={onRetry} icon={<RefreshCw size={15} />}>
              Erneut laden
            </Button>
          </div>
          <FallbackCatalogFields attributes={attributes} onChange={onChange} />
        </>
      )}

      <section className={`expert-json ${expertOpen ? "is-open" : ""}`}>
        <button
          type="button"
          className="expert-json__toggle"
          onClick={() => setExpertOpen((current) => !current)}
          aria-expanded={expertOpen}
        >
          <span><Code2 size={18} /></span>
          <div>
            <strong>JSON-Expertenmodus</strong>
            <small>Komplexe PTD-Attribute strukturiert ergänzen und in den Entwurf mergen</small>
          </div>
          {expertOpen ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
        </button>
        {expertOpen && (
          <div className="expert-json__body">
            <div className="expert-json__notice">
              <ShieldCheck size={17} />
              <p>
                Trage entweder das direkte <code>attributes</code>-Objekt oder{" "}
                <code>{`{"attributes": {...}}`}</code> ein. Gleichnamige Werte überschreiben
                den aktuellen Entwurf; Bestands-, Preis- und Versandfelder werden blockiert.
              </p>
            </div>
            <label>
              <span>Strukturierte Amazon-Attribute</span>
              <Textarea
                className="expert-json__editor"
                rows={15}
                spellCheck={false}
                value={expertJson}
                onChange={(event) => {
                  setExpertJson(event.target.value);
                  setExpertError(null);
                  setExpertSuccess(null);
                }}
                aria-label="JSON-Attribute"
              />
            </label>
            {expertError && (
              <div className="expert-json__message expert-json__message--error" role="alert">
                <AlertTriangle size={15} /> {expertError}
              </div>
            )}
            {expertSuccess && (
              <div className="expert-json__message expert-json__message--success">
                <Check size={15} /> {expertSuccess}
              </div>
            )}
            <div className="expert-json__actions">
              <span>{Object.keys(attributes).length} Attribute aktuell im Entwurf</span>
              <Button variant="ghost" onClick={formatExpertJson} icon={<Braces size={15} />}>
                JSON formatieren
              </Button>
              <Button onClick={applyExpertJson} icon={<Check size={15} />}>
                Validieren & mergen
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function DynamicPtdField({
  field,
  value,
  onChange,
}: {
  field: PtdField;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const validationError = validatePtdFieldValue(field, value);
  const label = `${field.label}${field.required ? " *" : ""}`;
  const hint = [
    field.description,
    field.maxLength ? `Maximal ${field.maxLength} Zeichen.` : null,
    field.minimum !== undefined ? `Minimum ${field.minimum}.` : null,
    field.maximum !== undefined ? `Maximum ${field.maximum}.` : null,
  ]
    .filter(Boolean)
    .join(" ");

  if (field.enumOptions.length) {
    return (
      <Field label={label} hint={hint || undefined} error={validationError ?? undefined}>
        <Select
          value={value === undefined ? "" : JSON.stringify(value)}
          onChange={(event) =>
            onChange(event.target.value ? JSON.parse(event.target.value) : undefined)
          }
        >
          <option value="">Bitte wählen …</option>
          {field.enumOptions.map((option) => (
            <option key={JSON.stringify(option.value)} value={JSON.stringify(option.value)}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>
    );
  }

  if (field.valueType === "boolean") {
    return (
      <Field label={label} hint={hint || undefined} error={validationError ?? undefined}>
        <Select
          value={typeof value === "boolean" ? String(value) : ""}
          onChange={(event) =>
            onChange(event.target.value === "" ? undefined : event.target.value === "true")
          }
        >
          <option value="">Nicht gesetzt</option>
          <option value="true">Ja</option>
          <option value="false">Nein</option>
        </Select>
      </Field>
    );
  }

  if (field.valueType === "number" || field.valueType === "integer") {
    return (
      <Field label={label} hint={hint || undefined} error={validationError ?? undefined}>
        <Input
          type="number"
          step={field.valueType === "integer" ? "1" : "any"}
          min={field.minimum}
          max={field.maximum}
          value={typeof value === "number" ? value : ""}
          onChange={(event) =>
            onChange(event.target.value === "" ? undefined : Number(event.target.value))
          }
        />
      </Field>
    );
  }

  const stringValue = typeof value === "string" ? value : "";
  const multiline = (field.maxLength ?? 0) > 300 || field.format === "multiline";
  return (
    <Field label={label} hint={hint || undefined} error={validationError ?? undefined}>
      {multiline ? (
        <Textarea
          rows={4}
          value={stringValue}
          minLength={field.minLength}
          maxLength={field.maxLength}
          onChange={(event) => onChange(event.target.value || undefined)}
        />
      ) : (
        <Input
          value={stringValue}
          minLength={field.minLength}
          maxLength={field.maxLength}
          onChange={(event) => onChange(event.target.value || undefined)}
        />
      )}
    </Field>
  );
}

function FallbackCatalogFields({
  attributes,
  onChange,
}: {
  attributes: Record<string, unknown>;
  onChange: (attributes: Record<string, unknown>) => void;
}) {
  const setAttribute = (key: string, value: unknown) => {
    const next = { ...attributes };
    if (value === "" || value === undefined) delete next[key];
    else next[key] = value;
    onChange(next);
  };

  return (
    <section className="ptd-fallback">
      <header>
        <Info size={17} />
        <div>
          <strong>Basisfelder als sichere Zwischenlösung</strong>
          <p>
            Diese kompakten Werte werden vom API Server in Amazon-Attributstrukturen
            normalisiert. Komplexe Pflichtangaben gehören in den Expertenmodus.
          </p>
        </div>
      </header>
      <div className="form-grid form-grid--two">
        <Field label="Produkttitel *" className="form-grid__wide">
          <Input
            value={stringValue(attributes.item_name)}
            onChange={(event) => setAttribute("item_name", event.target.value)}
          />
        </Field>
        <Field label="Marke *">
          <Input
            value={stringValue(attributes.brand)}
            onChange={(event) => setAttribute("brand", event.target.value)}
          />
        </Field>
        <Field label="Hersteller">
          <Input
            value={stringValue(attributes.manufacturer)}
            onChange={(event) => setAttribute("manufacturer", event.target.value)}
          />
        </Field>
        <Field label="EAN / GTIN *">
          <Input
            inputMode="numeric"
            value={stringValue(attributes.external_product_id)}
            onChange={(event) => setAttribute("external_product_id", event.target.value)}
          />
        </Field>
        <Field label="ID-Typ">
          <Select
            value={stringValue(attributes.external_product_id_type) || "ean"}
            onChange={(event) => setAttribute("external_product_id_type", event.target.value)}
          >
            <option value="ean">EAN</option>
            <option value="upc">UPC</option>
            <option value="gtin">GTIN</option>
          </Select>
        </Field>
        <Field label="Herkunftsland">
          <Input
            maxLength={2}
            value={stringValue(attributes.country_of_origin)}
            onChange={(event) =>
              setAttribute("country_of_origin", event.target.value.toUpperCase())
            }
            placeholder="z. B. DE"
          />
        </Field>
        <Field label="Produktbeschreibung" className="form-grid__wide">
          <Textarea
            rows={4}
            value={stringValue(attributes.product_description)}
            onChange={(event) => setAttribute("product_description", event.target.value)}
          />
        </Field>
      </div>
    </section>
  );
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function stringValue(value: unknown) {
  return typeof value === "string" ? value : "";
}
